import { isAxiosError } from 'axios';
import { apiService } from './api';
export type EhrDeliveryStatus = 'none' | 'queued' | 'delivering' | 'delivered' | 'failed';

type NoteParams = { appointmentId?: string; encounterId?: string };

export interface SendToEhrResult {
  deliveryId: string;
  status: string;
  error?: string;
}

/** Where the note's EHR delivery stands, from the clinical encounter's delivery columns. */
export interface EhrDeliveryState {
  status: EhrDeliveryStatus;
  error: string | null;
  canRetry: boolean;
  /** The last push found no webhook: nothing was sent, the results wait on the Pull API. */
  pullOnly: boolean;
  /**
   * The status column says none, but the audit trail shows the last push left (a note sent
   * before the column existed, or a queued write that failed). Sending again would duplicate it.
   */
  sentWithoutStatus: boolean;
}

function notePath(noteId: string, params: NoteParams): string {
  return params.encounterId
    ? `/v1/clinical/encounter/${params.encounterId}/medical-note/${noteId}`
    : `/v1/clinical/${params.appointmentId}/medical-note/${noteId}`;
}

class NotesService {
  /**
   * Send clinical results to connected EHR via webhook push.
   * Uses apiService so the request goes to the backend (API_BASE_URL), not the frontend origin.
   * A delivery the EHR side refused comes back as a 502 (AMP-109) and resolves here as status
   * 'failed' with the server's detail. Any other error rejects, including a 502 without that body:
   * a gateway timeout says nothing about whether the note went out.
   */
  async sendToEhr(noteId: string, params: NoteParams): Promise<SendToEhrResult> {
    try {
      const response = await apiService.post<{ success: boolean; data: SendToEhrResult }>(
        `${notePath(noteId, params)}/send-to-ehr`
      );
      return response?.data ?? { deliveryId: '', status: 'unknown' };
    } catch (error) {
      const body = isAxiosError(error) && error.response?.status === 502
        ? (error.response.data as { success?: boolean; data?: SendToEhrResult } | undefined)
        : undefined;
      if (body?.success === false && body.data?.status === 'failed') {
        return { deliveryId: body.data.deliveryId ?? '', status: 'failed', error: body.data.error };
      }
      throw error;
    }
  }

  /**
   * Read the note's EHR delivery status (AMP-109). Null when it cannot be read; the caller keeps
   * what it last knew.
   */
  async getEhrDeliveryState(noteId: string, params: NoteParams): Promise<EhrDeliveryState | null> {
    try {
      const response = await apiService.get<{
        success: boolean;
        data: {
          status?: EhrDeliveryStatus;
          error?: string | null;
          canRetry?: boolean;
          deliveries?: Array<{ details?: { status?: string; deliveryMode?: string } }>;
        };
      }>(`${notePath(noteId, params)}/delivery-status`);
      const data = response?.data;
      if (!data?.status) return null;
      // Audit entries come oldest first. A Pull API read is logged here too, but it is not a
      // delivery attempt and must not hide the last push (AMP-196).
      const pushes = (data.deliveries ?? []).filter((entry) => entry.details?.deliveryMode !== 'pull');
      const lastAttempt = pushes[pushes.length - 1]?.details?.status;
      const noColumn = data.status === 'none';
      return {
        status: data.status,
        error: data.error ?? null,
        canRetry: Boolean(data.canRetry),
        pullOnly: noColumn && lastAttempt === 'no_webhook',
        sentWithoutStatus: noColumn && (lastAttempt === 'queued' || lastAttempt === 'delivered'),
      };
    } catch {
      return null;
    }
  }

  /**
   * Check if EHR delivery is available for the current organization.
   * Uses apiService so the request goes to the backend (API_BASE_URL), not the frontend origin.
   */
  async checkEhrDeliveryAvailability(): Promise<{ available: boolean; reasons: string[] }> {
    try {
      const response = await apiService.get<{ success: boolean; data: { available: boolean; reasons: string[] } }>(
        '/v1/clinical/ehr-delivery/availability'
      );
      return response?.data ?? { available: false, reasons: [] };
    } catch {
      return { available: false, reasons: [] };
    }
  }
}

export const notesService = new NotesService();
export default notesService;
